/**
 * Coach-intelligence Rung 2 §2.1 — the ACTIVATION NONCE (the merged A/F6 rule).
 *
 * "The URL identifies; it does not activate or authorize by itself." A reaction
 * tap writes a nonce into the new history entry's state AND navigates with
 * `?run=`; the chat mount fetches context ONLY when a valid nonce for that
 * runId is present. Lifecycle (contracted): created on tap → survives refresh
 * of its history entry (history.state persists across reload) → cleared/
 * overwritten on every §2 END trigger → never re-mintable except by a new
 * reaction tap.
 *
 * Dismissal is TWO mechanisms, deliberately redundant:
 *   1. a sessionStorage dismissed-nonce set — the authority. It survives a
 *      reload (refresh mid-context re-activates: the nonce was never
 *      dismissed) and covers navigation-away, where the old history entry can
 *      no longer be rewritten (back-nav finds the nonce present but DISMISSED
 *      → ordinary chat, F6's non-resurrection).
 *   2. a dismissal marker written over the nonce in the CURRENT history entry
 *      (close × and in-page exits), per the contract's same-entry wording.
 *
 * React-router `navigate(to, { state })` stores state under history.state.usr;
 * readers here go to window.history.state directly so they never depend on a
 * router re-render.
 */

export interface RunContextActivation {
  runId: string;
  nonce: string;
  createdAt: number;
}

/** The §9.3 athlete-facing loss line (client-rendered; §6 approval-set member). */
export const RUN_CONTEXT_LOST_LINE =
  "I can't load that run anymore, but we can keep talking about your training.";

/** history.state key (builder-set default from the contract). */
export const ACTIVATION_STATE_KEY = 'runContextActivation';

const DISMISSED_STORE_KEY = 'tayro_run_ctx_dismissed';
// Review F2 — generous headroom: the FIFO-evicted consumed-set is a per-tab
// defense layer; 128 taps in one tab session before the oldest nonce ages out.
const DISMISSED_MAX = 128;

function randomNonce(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  return `n-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Mint a fresh activation for a reaction tap — the ONLY code path that mints
 *  one (§2.1). The caller navigates with `{ state: { runContextActivation } }`. */
export function mintActivation(runId: string): RunContextActivation {
  return { runId, nonce: randomNonce(), createdAt: Date.now() };
}

function readDismissedSet(): string[] {
  try {
    const raw = window.sessionStorage.getItem(DISMISSED_STORE_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return Array.isArray(parsed) ? parsed.filter((n): n is string => typeof n === 'string') : [];
  } catch {
    return [];
  }
}

/** True when this nonce was consumed by a §2 END trigger. */
export function isDismissed(nonce: string): boolean {
  return readDismissedSet().includes(nonce);
}

function rawActivationFromHistory(): unknown {
  if (typeof window === 'undefined') return null;
  const st = window.history.state as Record<string, unknown> | null;
  if (!st) return null;
  // react-router v6 keeps navigate() state under `usr`; a direct
  // history.pushState would carry it at the root. Accept both.
  const usr = st.usr as Record<string, unknown> | undefined;
  return (usr && usr[ACTIVATION_STATE_KEY]) ?? st[ACTIVATION_STATE_KEY] ?? null;
}

/**
 * §2.1 mount rule — the valid activation for THIS runId in the CURRENT history
 * entry, or null. Null ⇒ ordinary unseeded chat, regardless of what the URL
 * says (restored/bookmarked/shared URLs never had a nonce; dismissed entries
 * do not resurrect).
 */
export function readActivation(runId: string): RunContextActivation | null {
  const raw = rawActivationFromHistory();
  if (!raw || typeof raw !== 'object') return null;
  const a = raw as Partial<RunContextActivation> & { dismissedAt?: number };
  if (typeof a.nonce !== 'string' || a.nonce.length === 0) return null;
  if (typeof a.runId !== 'string' || a.runId !== runId) return null;
  if (typeof a.dismissedAt === 'number') return null; // same-entry dismissal marker
  if (isDismissed(a.nonce)) return null;
  return { runId: a.runId, nonce: a.nonce, createdAt: typeof a.createdAt === 'number' ? a.createdAt : 0 };
}

/**
 * §2 END triggers all funnel here: record the nonce as consumed (authoritative,
 * survives back-nav AND reload of the left-behind entry within this tab) and,
 * when the current entry still carries it, overwrite it with the dismissal
 * marker in place. Never throws.
 */
export function dismissActivation(nonce: string): void {
  if (!nonce) return;
  try {
    const set = readDismissedSet();
    if (!set.includes(nonce)) {
      set.push(nonce);
      while (set.length > DISMISSED_MAX) set.shift();
      window.sessionStorage.setItem(DISMISSED_STORE_KEY, JSON.stringify(set));
    }
  } catch {
    /* storage unavailable — the history marker below still covers this entry */
  }
  try {
    const st = window.history.state as Record<string, unknown> | null;
    const usr = st?.usr as Record<string, unknown> | undefined;
    const current = (usr && usr[ACTIVATION_STATE_KEY]) ?? st?.[ACTIVATION_STATE_KEY];
    const cur = current as Partial<RunContextActivation> | null;
    if (cur && cur.nonce === nonce) {
      writeActivationSlot({ runId: cur.runId, nonce, dismissedAt: Date.now() });
    }
  } catch {
    /* replaceState unavailable — the dismissed set above is the authority */
  }
}

/** Overwrite the CURRENT entry's activation slot in place (usr-aware). */
function writeActivationSlot(value: Record<string, unknown>): void {
  const st = window.history.state as Record<string, unknown> | null;
  const usr = st?.usr as Record<string, unknown> | undefined;
  const nextState = usr && usr[ACTIVATION_STATE_KEY] !== undefined
    ? { ...st, usr: { ...usr, [ACTIVATION_STATE_KEY]: value } }
    : { ...st, [ACTIVATION_STATE_KEY]: value };
  window.history.replaceState(nextState, '');
}

/**
 * Review F2 — SYNCHRONOUS departing-entry marker. Called in the unmount
 * cleanup BEFORE any deferred work: when the cleanup still runs on the
 * activation's own entry (unmount without navigation, some transition
 * orders), the marker lands in that entry so a back-nav finds it dismissed
 * even if the FIFO consumed-set has aged the nonce out. When history has
 * already moved (typical router navigation), the nonce guard makes this a
 * safe no-op and the consumed-set remains the authority.
 * @returns true when the marker was written (the entry carried this nonce)
 */
export function markEntryDismissedSync(activation: RunContextActivation): boolean {
  try {
    const raw = rawActivationFromHistory() as Partial<RunContextActivation> | null;
    if (!raw || raw.nonce !== activation.nonce) return false;
    writeActivationSlot({ runId: activation.runId, nonce: activation.nonce, dismissedAt: Date.now() });
    return true;
  } catch {
    return false;
  }
}

/**
 * Review F2 — the StrictMode undo for markEntryDismissedSync. The dev
 * double-mount runs cleanup (which marks the entry) and then re-runs the
 * effect on the SAME entry; if that entry carries OUR marker (nonce match),
 * restore the original activation so the context re-activates. A real
 * transition never matches (different entry / different nonce) and restores
 * nothing.
 * @returns true when the activation was restored to the current entry
 */
export function restoreActivationToEntry(activation: RunContextActivation): boolean {
  try {
    const raw = rawActivationFromHistory() as
      | (Partial<RunContextActivation> & { dismissedAt?: number })
      | null;
    if (!raw || raw.nonce !== activation.nonce || typeof raw.dismissedAt !== 'number') return false;
    writeActivationSlot({ runId: activation.runId, nonce: activation.nonce, createdAt: activation.createdAt });
    return true;
  } catch {
    return false;
  }
}

/**
 * A reaction tap's target: the training-coach chat href carrying `?run=`.
 * Returns the runId when (and only when) this href is a Rung-2 entry point;
 * anything else (generic /v2/chat, other routes) is not a reaction tap.
 */
export function parseRunIdFromCoachHref(href: string | null | undefined): string | null {
  if (!href) return null;
  try {
    const u = new URL(href, 'http://local');
    if (u.pathname !== '/v2/chat') return null;
    const runId = u.searchParams.get('run');
    return runId && runId.length > 0 ? runId : null;
  } catch {
    return null;
  }
}

/**
 * ⚖️ §2.1 — THE SECOND MINTING PATH: a PUSH TAP (founder-ruled 2026-08-10).
 *
 * Until now `useReactionTapActivation` was the only code path that minted an
 * activation, and §2.1's rule is "the URL identifies; it does not activate or
 * authorize by itself" — written to stop RESTORED, BOOKMARKED and SHARED URLs
 * from seeding a run conversation.
 *
 * A notification tap is not that class of URL. The route comes from OUR OWN
 * dispatcher, for a run we froze, to the device the grant belongs to, and it is
 * already sanitized through the href allowlist before it reaches here. Its
 * provenance is the in-app anchor's: a first-party, app-generated event. So
 * minting here honors the rule's intent while removing the dead step the
 * founder named — banner → Today → find the note → tap it.
 *
 * ⚠️ RECORDED AS A CONTRACT EXTENSION, not a silent one: coach-intelligence-r2
 * §2.1 names ONE minting path and this makes two. It takes ratification at the
 * next contract touch. The safety property it must preserve — and does — is
 * that nothing mints from a URL alone; every mint still originates in a
 * first-party event this app dispatched.
 *
 * Accepts the router-relative form (`/chat?…`, what the push carries) and the
 * web-absolute form (`/v2/chat?…`); returns the `navigate()` state object, or
 * null. Null ⇒ the caller navigates normally and the chat opens unseeded —
 * exactly today's behavior, never worse.
 */
export function activationStateForPushRoute(
  route: string | null | undefined,
): { runContextActivation: RunContextActivation } | null {
  if (!route) return null;
  try {
    const u = new URL(route, 'http://local');
    if (u.pathname !== '/chat' && u.pathname !== '/v2/chat') return null;
    const runId = u.searchParams.get('run');
    if (!runId) return null;
    return { runContextActivation: mintActivation(runId) };
  } catch {
    return null;
  }
}

/**
 * The ROUTER-relative path for a reaction href. The server href is absolute
 * (`/v2/chat?…`), but the chat Route is `path="/chat"` under the router
 * basename (`/v2` on web, `/` native) — navigate() must receive the
 * basename-relative form or the web app double-prefixes to /v2/v2/chat.
 */
export function coachChatRouterPath(href: string | null | undefined): string | null {
  if (!parseRunIdFromCoachHref(href)) return null;
  try {
    const u = new URL(href as string, 'http://local');
    return `/chat${u.search}`;
  } catch {
    return null;
  }
}
